import { IOption } from '@shared/models/icommons.model';
import { FilterTypes } from '@records/records-search/filter-set/filter-set.model';
import type { CompoundFilterConfig } from '@cbre/components';
import { QueryCriteria } from './search-filters.model';
import { CompoundFilterBuilder, CompoundFilterType } from './compound-filter.types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Builds a QueryCriteria[] of selected compound filters from the catalog,
 * containing only the fields needed for storage and wire transport.
 * Values are NOT included here — they are injected later by injectCompoundFilterValues.
 */
const buildSelectedCompoundFilters = (catalogFilters: IOption[], selectedKeys: number[]): QueryCriteria[] =>
  catalogFilters
    .filter(cf => selectedKeys.includes(cf.key))
    .map(({ key, field, type, dataType }) => ({ key, field, type, dataType }) as QueryCriteria);

/** Reads the keys off a previously-built QueryCriteria[] of selected compound filters. */
const readKeysFrom = (compoundFilters: QueryCriteria[] | undefined): number[] | undefined =>
  compoundFilters?.length ? compoundFilters.map(cf => cf.key) : undefined;

// ---------------------------------------------------------------------------
// Builders
// ---------------------------------------------------------------------------

/**
 * Standalone builder — compound filters live directly on the root QueryCriteria.
 * Used for text/ID filters that have no associations.
 */
const standaloneBuilder: CompoundFilterBuilder = {
  applySelection(parentFilter, _subFilterKey, selectedKeys, catalogFilters) {
    return { ...parentFilter, compoundFilters: buildSelectedCompoundFilters(catalogFilters, selectedKeys) };
  },
  readSelectedKeys(parentFilter) {
    return readKeysFrom(parentFilter.compoundFilters as QueryCriteria[]);
  },
};

/**
 * Associated-text builder — compound filters live on the specific sub-filter
 * identified by subFilterKey inside the group's values[] array.
 * Used for group filters where the text input carries the compound options.
 */
const associatedTextBuilder: CompoundFilterBuilder = {
  applySelection(parentFilter, subFilterKey, selectedKeys, catalogFilters) {
    if (!Array.isArray(parentFilter.values)) return parentFilter;

    const compoundFilters = buildSelectedCompoundFilters(catalogFilters, selectedKeys);
    return {
      ...parentFilter,
      values: (parentFilter.values as QueryCriteria[]).map(v =>
        v.key === subFilterKey ? { ...v, compoundFilters } : v
      ),
    };
  },
  readSelectedKeys(parentFilter, subFilterKey) {
    if (!Array.isArray(parentFilter.values)) return undefined;
    const sub = (parentFilter.values as QueryCriteria[]).find(v => v.key === subFilterKey);
    return readKeysFrom(sub?.compoundFilters as QueryCriteria[]);
  },
};

/**
 * NAICS builder — two wire positions depending on the parent filter's type:
 *
 * - type === 'group' (e.g. Lease Tenant NAICS): compound filters live on the matching
 *   sub-filter inside values[]. This is the same wire position as associatedTextBuilder.
 * - any other type (e.g. Company / Property NAICS Code, type 'multi'): compound filters
 *   live directly on the root QueryCriteria, same as standaloneBuilder. These filters carry
 *   their user selection as a primitive/array value (e.g. ['42','43']), not as QueryCriteria
 *   sub-filters, so the group path must not be taken — checking Array.isArray(values) is
 *   insufficient because a selected NAICS code array also passes that check.
 */
const naicsBuilder: CompoundFilterBuilder = {
  applySelection(parentFilter, subFilterKey, selectedKeys, catalogFilters) {
    const compoundFilters = buildSelectedCompoundFilters(catalogFilters, selectedKeys);
    if (parentFilter.type !== FilterTypes.Group) {
      return { ...parentFilter, compoundFilters };
    }
    return {
      ...parentFilter,
      values: (parentFilter.values as QueryCriteria[]).map(v =>
        v.key === subFilterKey ? { ...v, compoundFilters } : v
      ),
    };
  },
  readSelectedKeys(parentFilter, subFilterKey) {
    if (parentFilter.type === FilterTypes.Group && Array.isArray(parentFilter.values)) {
      const sub = (parentFilter.values as QueryCriteria[]).find(v => v.key === subFilterKey);
      if (sub !== undefined) return readKeysFrom(sub.compoundFilters as QueryCriteria[]);
    }
    return readKeysFrom(parentFilter.compoundFilters as QueryCriteria[]);
  },
};

/** Fallback — returned for filters that carry no compound filters. */
const noopBuilder: CompoundFilterBuilder = {
  applySelection: (parentFilter: QueryCriteria) => parentFilter,
  readSelectedKeys: () => undefined,
};

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

const COMPOUND_FILTER_BUILDERS: ReadonlyMap<CompoundFilterType, CompoundFilterBuilder> = new Map([
  [CompoundFilterType.Standalone, standaloneBuilder],
  [CompoundFilterType.AssociatedText, associatedTextBuilder],
  [CompoundFilterType.Naics, naicsBuilder],
]);

/**
 * Resolves the CompoundFilterType for a given filter based on its
 * `inputType` and `associations` fields.
 */
export const resolveCompoundFilterType = (filter: Pick<IOption, 'inputType' | 'associations'>): CompoundFilterType => {
  if (filter.inputType?.toLowerCase() === 'naics') return CompoundFilterType.Naics;
  if (filter.associations?.length > 0) return CompoundFilterType.AssociatedText;
  return CompoundFilterType.Standalone;
};

/**
 * Returns the CompoundFilterBuilder responsible for this filter's type.
 * Falls back to noopBuilder for filters that have no compound filters defined.
 */
export const getCompoundFilterBuilder = (filter: Pick<IOption, 'inputType' | 'associations'>): CompoundFilterBuilder =>
  COMPOUND_FILTER_BUILDERS.get(resolveCompoundFilterType(filter)) ?? noopBuilder;

// ---------------------------------------------------------------------------
// Default-key resolution (out-of-the-box behavior — configs are customizations, not requirements)
// ---------------------------------------------------------------------------

/**
 * Resolves the keys to pre-select the FIRST time a filter (that has compound filters) is
 * added and no prior selection exists yet — e.g. picked from the "Add Filters" modal.
 *
 * Priority:
 * 1. `config.initialSelectedKeys` when explicitly configured.
 * 2. ALL catalog compound-filter keys — for EVERY filter, configured or not. Adding a filter
 *    always searches on every available compound option by default; `defaultSelectedKeys`
 *    (the deselect-all guard) is a deliberately separate, independent setting and must NOT
 *    influence this fallback. Only an explicit `initialSelectedKeys` should ever narrow it.
 */
export const resolveInitialSeedKeys = (catalogFilters: IOption[], config?: CompoundFilterConfig): number[] => {
  if (config?.initialSelectedKeys?.length) return config.initialSelectedKeys;
  return catalogFilters.map(cf => cf.key);
};

/**
 * Resolves the minimum keys enforced when:
 * - the user deselects every compound-filter option (guard in CompoundFilterSelectionComponent), or
 * - a filter that already existed before it gained compound filters is being restored/migrated
 *   (page reload, saved search) with no selection stored yet.
 *
 * Priority:
 * 1. `config.defaultSelectedKeys` when configured.
 * 2. Out-of-the-box (no config at all): the FIRST catalog compound-filter key.
 */
export const resolveDefaultKeys = (catalogFilters: IOption[], config?: CompoundFilterConfig): number[] => {
  if (config?.defaultSelectedKeys?.length) return config.defaultSelectedKeys;
  return catalogFilters.length ? [catalogFilters[0].key] : [];
};

// ---------------------------------------------------------------------------
// Value injection utility
// ---------------------------------------------------------------------------

/**
 * Recursively injects the parent filter's `values` onto each selected compound filter
 * before the QueryCriteria[] is sent to the search API.
 *
 * Rules:
 * - If a filter has `compoundFilters` and a non-null `values`: copies the parent value
 *   onto every compound filter entry.
 * - If a filter has `type === 'group'` and a `values` array: recurses into the array
 *   so that sub-filters (AssociatedText / NAICS) are also processed.
 * - Otherwise: returns the filter unchanged.
 *
 * This function is pure and idempotent — safe to call multiple times.
 */
export const injectCompoundFilterValues = (filter: QueryCriteria): QueryCriteria => {
  // Inject on root-level compound filters (Standalone case)
  if (filter.compoundFilters?.length && filter.values != null && filter.values !== '') {
    return {
      ...filter,
      compoundFilters: filter.compoundFilters.map(cf => ({ ...cf, values: filter.values })),
    };
  }

  // Recurse into group values[] so sub-filters (AssociatedText / NAICS) are processed
  if (filter.type === FilterTypes.Group && Array.isArray(filter.values)) {
    return { ...filter, values: (filter.values as QueryCriteria[]).map(injectCompoundFilterValues) };
  }

  return filter;
};
